import { useDeleteApi } from '@/apiError/apiDelete';
import { useGetApiList, useMutateGetApi } from '@/apiError/apiShow';
import '@/assets/style/companyList.scss';
import '@/assets/style/modern-group-list.css';
import { getBsFromAD } from '@/common/custom-date-selector-list/helpers';
import LedgerForm from '@/components/forms/master/account/ledger/LedgerForm';
import useAppData from '@/hooks/useAppData';
import CreateModalButton from '@/utils/components/CreateModelButton';
import DeleteModalButton from '@/utils/components/DeleteModalButton';
import SearchBox from '@/utils/components/SearchBox';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import GlobalModal from '@/utils/modal/Modal';
import {
  fetchPermissions,
  hasPermission,
} from '@/utils/permissions/userPermission';
import { globalSearch } from '@/utils/search/Search';
import DynamicTable from '@/utils/table/DynamicTable';
import { ledgerTableHeader as baseColumns } from '@/utils/tableHeader';
import { showToast } from '@/utils/toastMessage/ToastMessage';
import { EditFilled } from '@ant-design/icons';

import { Card, Col, Form, Row } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

/**
 * Ledger Component
 * Displays and manages ledger data with searches , delete and edit functionalities.
 *
 * @component
 * @returns {React.ReactElement} Ledger Management interface.
 */

const Ledger = () => {
  const [form] = Form.useForm();
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [searchTerm, setSearchTrem] = useState('');
  const [deleteReferenceId, setDeleteReferenceId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [selectedLedger, setSelectedLedger] = useState(null); // Holds data for the ledger to edit
  const navigate = useNavigate();
  const {
    data: { data: ledgerData } = {},
    isPending: loading,
    refetch,
  } = useGetApiList('account/ledgers/');
  const { mutateAsync: deleteData, isPending: deleteLoading } = useDeleteApi();
  const { mutateAsync: getDataById, isPending: dataFetchLoading } =
    useMutateGetApi();

  /**
   * Shows delete confirmation modal.
   * @param {string} referenceId-The referenceId of the ledger to delete
   */
  const showCancelModal = (referenceId, isLedger) => {
    if (isLedger === true) {
      showToast("Pre-defined ledger can't be deleted", 'warn');
      return;
    }
    setModalType('delete');
    setIsMessageModalVisible(true);
    setDeleteReferenceId(referenceId);
  };

  /**
   * Hides the modal
   */
  const handleCancel = () => {
    setIsMessageModalVisible(false);
  };

  const openModal = (mode, ledger = null) => {
    setModalMode(mode);
    if (mode === 'edit' && ledger) {
      fetchInitialEditData(ledger);
      setSelectedLedger(ledger); // Set the full ledger object
    } else {
      setSelectedLedger(null);
    }
    setIsModalVisible(true);
  };

  const closeModal = () => {
    form.resetFields();
    setIsModalVisible(false);
    setSelectedLedger(null);
  };

  /**
   *
   * Deletes the selected ledger item.
   */
  const deleteLedger = async () => {
    if (!deleteReferenceId) return;
    setIsMessageModalVisible(false);
    try {
      // setLoading(true);
      // await DeleteAllData.deleteData(`/account/ledger/${deleteReferenceId}/`);
      await deleteData(`/account/ledger/${deleteReferenceId}/`);
      // fetchLedgerData();
      refetch();
    } finally {
      setIsMessageModalVisible(false);
    }
  };

  /**
   *Handles search input change.

   * @param {string} value-The search term.
   */
  const handleSearch = (value) => {
    setSearchTrem(value);
  };

  const filteredTableData = globalSearch(ledgerData, searchTerm);
  useEffect(() => {
    // fetchLedgerData();
    refetch();
    setPermissions(fetchPermissions());
  }, []);
  /**
   * Fetches ledger data from API.
   */
  // const fetchLedgerData = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await ShowAllData.showDetails(
  //       'account/ledgers/',
  //       navigate,
  //     );
  //     setLedgerData(response.data);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const handleEditClick = (ledger, isDefault) => {
    if (isDefault === true) {
      showToast("Pre-defined account group can't be edited", 'warn');
      return;
    }
    openModal('edit', ledger);
  };

  const { app } = useAppData();

  const fetchInitialEditData = async (referenceId) => {
    if (referenceId) {
      // const response = await ShowDataById.getDataById(
      //   `/account/ledger/${referenceId}/`,
      //   navigate,
      // );
      const response = await getDataById({
        url: `/account/ledger/${referenceId}/`,
      });
      const dataToPut = response?.data;

      // form.setFieldsValue(dataToPut);
      form.setFieldsValue({
        ...dataToPut,
        dobPrefix: app?.dateType || 'bs',
        dob_ad: dataToPut?.dob && dayjs(dataToPut?.dob),
        aniversaryDate_ad: dataToPut?.anniversaryDate
          ? dayjs(dataToPut?.anniversaryDate)
          : null,
        aniversaryDate_bs: dataToPut?.anniversaryDate
          ? getBsFromAD(dataToPut?.anniversaryDate)
          : null,
        dob_bs: dataToPut?.dob && getBsFromAD(dataToPut?.dob),
      });
    }
  };

  const columns = useMemo(() => {
    const cols = [...baseColumns];
    if (
      hasPermission(permissions, 'Ledger', 'isUpdate') ||
      hasPermission(permissions, 'Ledger', 'isDelete')
    ) {
      cols.push({
        title: 'Action',
        key: 'action',
        align: 'center',
        width: '10%',
        fixed: 'right',

        render: (_, ledger) => (
          <div className="ant-action" style={{ display: 'flex' }}>
            {hasPermission(permissions, 'Ledger', 'isUpdate') && (
              <div
                className="ant-button-edit"
                onClick={() =>
                  handleEditClick(ledger?.referenceId, ledger?.isDefault)
                }
              >
                <Link size="medium" className="edit">
                  <EditFilled />
                </Link>
              </div>
            )}
            {hasPermission(permissions, 'Ledger', 'isDelete') && (
              <DeleteModalButton
                showCancelModal={(id) => showCancelModal(id, ledger.isDefault)}
                data={ledger?.referenceId}
              />
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [permissions]);

  return (
    <div style={{ position: 'relative', top: 0, marginTop: 0, paddingTop: 0 }}>
      <div className="table">
        <Row gutter={[24, 0]}>
          <Col xs={24} xl={24}>
            <Card
              variant="borderless"
              className="circlebox tablespace mb-24"
              title={
                <>
                  {hasPermission(permissions, 'Ledger', 'isCreate') && (
                    <CreateModalButton
                      title="Create New Ledger"
                      openModal={openModal}
                    />
                  )}
                </>
              }
              extra={
                <SearchBox
                  searchPlaceholder="Search ledger..."
                  handleSearch={handleSearch}
                />
              }
            >
              <div className="table-responsive">
                <DynamicTable
                  data={filteredTableData}
                  columns={columns}
                  loading={loading || deleteLoading}
                  pagination={false}
                  isShowDynamicScroll={true}
                  scrollY
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      <GlobalModal
        isVisible={isMessageModalVisible}
        type={modalType}
        onConfirm={deleteLedger}
        onClose={handleCancel}
      />
      <GlobalFormModal
        isVisible={isModalVisible}
        title={modalMode === 'create' ? 'Create Ledger' : 'Update Ledger'}
        onClose={() => {
          closeModal();
          form.resetFields();
        }}
        loading={false}
      >
        <LedgerForm
          mode={modalMode}
          referenceId={selectedLedger}
          onClose={closeModal}
          refreshTable={refetch}
          ledgerData={ledgerData}
          isModalVisible={isModalVisible}
          loading={dataFetchLoading}
          form={form}
        />
      </GlobalFormModal>
    </div>
  );
};

export default Ledger;
